import React, {
    Children,
    cloneElement,
    ForwardedRef,
    MouseEventHandler,
    ReactNode,
    useCallback,
    useContext,
    useMemo,
    useRef,
    forwardRef,
} from 'react';
import { useMenuTriggerState } from 'react-stately';
import {
    chain,
    mergeProps,
    useFocusVisible,
    useHover,
    useInteractOutside,
    useMenu,
    useMenuItem,
    useMenuSection,
    usePress,
} from 'react-aria';
import { useLogger, Loggable, LoggerClickHandler } from '@direct-frontend/modules-logger';
import { genericMemo, mergeRefs } from '@direct-frontend/stdlib';
import { Node as AriaNode } from '@react-types/shared';
import { isMobile } from '@direct-frontend/platform-code';

import { Popup } from '../Popup';
import { getScrollbarClassName } from '../../lib/css/getScrollbarClassName';
import { useObjectRef } from '../../hooks/useObjectRef/useObjectRef';
import { ListBoxBase } from '../ListBoxBase/ListBoxBase';
import { ListSection } from '../List';
import { ListContext } from '../List/utils';
import { getDefaultRenderer } from '../ListBoxBase/Render/Render';
import {
    cnListBox,
    cnListBoxItemSeparator,
    cnListBoxSeparator,
    containsIconLeft,
    getDisabledKeys,
    isBorderElement,
    ListContext as ListBoxContext,
    useItemClasses,
} from '../ListBoxBase/utils';
import { SectionContent } from '../ListBoxBase/SectionContent/SectionContent';
import { isSection } from '../ListBoxBase/Item/utils';
import { TestProps } from '../../internal/types/testTypes';
import { Drawer, DrawerHeader } from '../Drawer';
import { Separator } from '../ListBoxBase/Separator/Separator';
import { TooltipContainerContext } from '../Tooltip/Tooltip';

import { MenuItem as MenuAnyItem, MenuItemSection, MenuItemSubMenu, MenuProps, MenuSingleItem } from './types';
import { cnMenu, cnMenuItem, getMenuItemKey, isMenuLink, isMenuOption, isMenuSection, isSubMenu } from './utils';
import { MenuOption } from './MenuOption/MenuOption';
import { MenuLink } from './MenuLink/MenuLink';
import { MenuIcon } from './MenuIcon/MenuIcon';
import { MenuState, useMenuState } from './useMenuState';
import { MenuContext } from './context';

import './Menu.css';

const SUBMENU_OPEN_CLOSE_DELAY = 200;

//<editor-fold desc="SubMenu">
interface SubMenuProps<ValueType extends string, SectionType extends string> extends TestProps {
    item: MenuItemSubMenu<ValueType>;
    state: MenuState<MenuAnyItem<ValueType, SectionType>>;
    label?: string;
    children: ReactNode;
    logClick?: LoggerClickHandler;
    onClose?: () => void;
    subMenuTrigger: 'click' | 'hover';
    closeMenuTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
    wasSubMenuHoveredRef: React.MutableRefObject<boolean>;
}

const SubMenu = <ValueType extends string, SectionType extends string>(props: SubMenuProps<ValueType, SectionType>) => {
    const {
        item,
        state,
        onClose,
        children,
        label,
        'data-testid': dataTestId,
        logClick,
        subMenuTrigger,
        closeMenuTimeoutRef,
        wasSubMenuHoveredRef,
    } = props;
    const { onAction, key } = item;
    const { size } = useContext(ListBoxContext);
    const ref = useRef<HTMLDivElement>(null);
    const popupRef = useRef(null);
    const { isFirst, isLast } = isBorderElement<
        ValueType,
        SectionType,
        MenuSingleItem<ValueType>,
        MenuItemSection<ValueType, SectionType>
    >(
        {
            ...item,
            value: key,
        },
        state.collection,
        getMenuItemKey,
    );
    const node = state.collection.getItem(key);

    const { menuItemProps, isDisabled, isFocused } = useMenuItem(
        {
            key,
            onAction,
            closeOnSelect: false,
        },
        state,
        ref,
    );

    const menuState = useMenuTriggerState({
        ...props,
        isOpen: Boolean(state.openedSubmenus[key]),
        onOpenChange: () => {
            state.toggleSubmenu(key, popupRef);
        },
        trigger: 'press',
    });
    const { pressProps } = usePress({
        onPress: () => {
            if (!isMobile() && subMenuTrigger === 'hover' && menuState.isOpen) {
                return;
            }

            state.toggleSubmenu(key, popupRef);
        },
        isDisabled: isDisabled,
    });

    const openTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null> = useRef(null);
    const closeTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null> = useRef(null);
    const wasSubMenuTriggerHovered: React.MutableRefObject<boolean> = useRef(false);
    const { hoverProps, isHovered } = useHover({
        // Засекаем таймаут открытия подменю. Открываем не сразу, так как возможно пользователь ведет мышку
        // по диагонали к попапу
        // Также сбрасываем таймаут закрытия меню – если пользователь вернул курсор с меню следующего уровня
        // на пункт, его открывающий
        onHoverStart: () => {
            wasSubMenuTriggerHovered.current = true;

            if (openTimeoutRef.current) {
                clearTimeout(openTimeoutRef.current);
                openTimeoutRef.current = null;
            }
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }
            // Костыль – setTimeout нужен, чтобы это действие случилось позже,
            // чем события наведения на предыдущий уровень меню,
            // которое засекает таймаут закрытия меню следующего уровня
            setTimeout(() => {
                if (closeMenuTimeoutRef.current) {
                    clearTimeout(closeMenuTimeoutRef.current);
                    closeMenuTimeoutRef.current = null;
                }
            });

            if (!menuState.isOpen) {
                openTimeoutRef.current = setTimeout(() => {
                    state.openSubmenu(key, popupRef);
                }, SUBMENU_OPEN_CLOSE_DELAY);
            }
        },
        // Засекаем таймаут закрытия подменю. Закрываем не сразу, так как возможно пользователь ведет мышку к попапу
        onHoverEnd: () => {
            if (openTimeoutRef.current) {
                clearTimeout(openTimeoutRef.current);
                openTimeoutRef.current = null;
            }
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
            }

            if (menuState.isOpen) {
                closeTimeoutRef.current = setTimeout(() => {
                    state.closeSubmenus();
                }, SUBMENU_OPEN_CLOSE_DELAY);
            }
        },
        isDisabled,
    });

    const popupContentRef = useRef<HTMLDivElement>(null);
    // Сбрасываем таймаут закрытия подменю, так как мы на него навелись и оно должно остаться
    // Костыль – сделано через mouseMove, так как hoverStart может не срабатывать второй раз в некоторых кейсах:
    // Когда не срабатывает – если в подменю есть еще одно подменю и курсор убрали с вложенного подменю
    // В этом случае техничеси hoverEnd не случился, поэтому hoverStart тоже не сработает
    const onPopupMouseMove: MouseEventHandler<HTMLDivElement> = useCallback(
        event => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- target можно так использовать
            if (popupContentRef.current?.contains(event.target as Node)) {
                if (wasSubMenuTriggerHovered.current) {
                    wasSubMenuTriggerHovered.current = false;

                    if (closeTimeoutRef.current) {
                        clearTimeout(closeTimeoutRef.current);
                        closeTimeoutRef.current = null;
                    }
                    wasSubMenuHoveredRef.current = true;
                }
            }
        },
        [wasSubMenuHoveredRef],
    );

    const isSubMenuOnHover = !isMobile() && subMenuTrigger === 'hover';
    const { isFocusVisible } = useFocusVisible();
    const { className } = useItemClasses({
        isFocused: (isFocusVisible && isFocused) || (isHovered && isSubMenuOnHover) || menuState.isOpen,
        isDisabled,
        isFirst,
        isLast,
    });

    const withSeparator = isMobile() && !isLast;

    const trigger = (
        <div
            ref={ref}
            className={cnMenuItem('sub-menu', { open: menuState.isOpen }, [
                className,
                cnMenuItem({ 'has-icon-right': true }),
            ])}
            data-testid={dataTestId}
            {...mergeProps(menuItemProps, pressProps, isSubMenuOnHover ? hoverProps : {})}
        >
            {children}
            <MenuIcon data-testid="SubmenuTrigger" type={'subMenu'} />
        </div>
    );

    const content = useMemo(() => {
        const menuList = (
            <MenuListWrapper
                size={size}
                items={item.items}
                onClose={onClose}
                aria-label={label || 'Menu'}
                width={isMobile() ? 'max' : 'auto'}
                data-testid={dataTestId && `${dataTestId}.Menu`}
                logClick={logClick}
                subMenuTrigger={subMenuTrigger}
                ref={popupContentRef}
                onMouseMove={isSubMenuOnHover ? onPopupMouseMove : undefined}
            />
        );

        if (isMobile()) {
            const title = item.title ?? node?.textValue ?? (typeof children === 'string' ? children : item.key);

            return (
                <Drawer
                    header={
                        <DrawerHeader
                            onCancelClick={menuState.close}
                            title={title}
                            data-testid={`${dataTestId ?? 'Menu'}.Header`}
                        />
                    }
                    isOpen={menuState.isOpen}
                    onClose={menuState.close}
                    data-testid={`${dataTestId ?? 'Menu'}.Drawer`}
                >
                    {menuList}
                </Drawer>
            );
        }

        return (
            <Popup
                ref={popupRef}
                isOpen={menuState.isOpen}
                onClose={menuState.close}
                ariaType={'menu'}
                triggerRef={ref}
                placement={['right-start', 'right-end', 'left-start', 'left-end']}
                offset={0}
                crossOffset={-8}
                className={cnMenu('sub-menu-popup')}
            >
                {menuList}
            </Popup>
        );
    }, [
        children,
        dataTestId,
        item.items,
        item.key,
        item.title,
        label,
        menuState.close,
        menuState.isOpen,
        node?.textValue,
        onClose,
        logClick,
        size,
        subMenuTrigger,
        onPopupMouseMove,
        isSubMenuOnHover,
    ]);

    return (
        <>
            {trigger}
            {content}
            {withSeparator && <Separator className={cnListBoxItemSeparator(null)} />}
        </>
    );
};
//</editor-fold>

//<editor-fold desc="MenuSection">
interface MenuSectionProps<ValueType extends string> {
    item: MenuItemSection<ValueType>;
    state: MenuState<MenuAnyItem<ValueType>>;
    children: ReactNode;
    childNodes: Iterable<AriaNode<MenuAnyItem<ValueType>>>;
    //Для закрытия меню
    onClose?: () => void;
    baseTestId?: string;
    subMenuTrigger: 'click' | 'hover';
    closeMenuTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
    wasSubMenuHoveredRef: React.MutableRefObject<boolean>;
}

const MenuSection = <ValueType extends string>(props: MenuSectionProps<ValueType>) => {
    const {
        onClose,
        item,
        state,
        childNodes,
        children,
        baseTestId = 'MenuSection',
        subMenuTrigger,
        closeMenuTimeoutRef,
        wasSubMenuHoveredRef,
    } = props;
    const { headingProps, itemProps, groupProps } = useMenuSection({ heading: children, 'aria-label': item.title });
    const withSeparator = item.key !== state.collection.getFirstKey();

    // Добавляем еще один сепаратор, если после секции идут элементы без секции
    const sectionLastItem = item.options[item.options.length - 1];
    const sectionLastItemKey = sectionLastItem && getMenuItemKey(sectionLastItem);
    const nextItemKey = sectionLastItemKey && state.collection.getKeyAfter(sectionLastItemKey);
    const nextItem = nextItemKey ? state.collection.getItem(nextItemKey) : null;
    const withLastSeparator = nextItem?.value && !isMenuSection(nextItem.value);

    const options = Array.from(childNodes).map(node => (
        <MenuItem
            key={node.key}
            node={node}
            state={state}
            onClose={onClose}
            baseTestId={baseTestId}
            subMenuTrigger={subMenuTrigger}
            closeMenuTimeoutRef={closeMenuTimeoutRef}
            wasSubMenuHoveredRef={wasSubMenuHoveredRef}
        />
    ));

    return (
        <>
            <SectionContent
                title={children}
                headingProps={headingProps}
                itemProps={itemProps}
                groupProps={groupProps}
                withSeparator={withSeparator}
                data-testid={`${baseTestId}.${item.key}`}
                help={item.help}
                iconLeft={item.icon}
            >
                {options}
            </SectionContent>
            {withLastSeparator && <Separator className={cnListBoxSeparator(null)} />}
        </>
    );
};
//</editor-fold>

//<editor-fold desc="MenuItem">
interface MenuItemProps<ValueType extends string> {
    node: AriaNode<MenuAnyItem<ValueType>>;
    state: MenuState<MenuAnyItem<ValueType>>;
    baseTestId?: string;
    //Для закрытия меню
    onClose?: () => void;
    subMenuTrigger: 'click' | 'hover';
    closeMenuTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
    wasSubMenuHoveredRef: React.MutableRefObject<boolean>;
}

const MenuItem = <ValueType extends string>(props: MenuItemProps<ValueType>) => {
    const {
        node,
        state,
        baseTestId = 'MenuItem',
        onClose,
        subMenuTrigger,
        closeMenuTimeoutRef,
        wasSubMenuHoveredRef,
    } = props;
    const item = node.value;

    const key = item ? getMenuItemKey(item) : null;
    const nodeInfo = useMemo(() => {
        return {
            name: key ? `MenuListItem.${key}` : 'MenuListItem',
            attrs: { key },
        };
    }, [key]);
    const { LogProvider, logClick } = useLogger(nodeInfo);

    if (!item) {
        return null;
    }

    if (isSection(item)) {
        return (
            <LogProvider>
                <MenuSection
                    item={item}
                    state={state}
                    onClose={onClose}
                    childNodes={node.childNodes}
                    baseTestId={baseTestId}
                    subMenuTrigger={subMenuTrigger}
                    closeMenuTimeoutRef={closeMenuTimeoutRef}
                    wasSubMenuHoveredRef={wasSubMenuHoveredRef}
                >
                    {node.rendered}
                </MenuSection>
            </LogProvider>
        );
    }

    if (isMenuOption(item)) {
        return (
            <LogProvider>
                <MenuOption item={item} state={state} data-testid={`${baseTestId}.${item.value}`}>
                    {node.rendered}
                </MenuOption>
            </LogProvider>
        );
    }

    if (isMenuLink(item)) {
        return (
            <LogProvider>
                <MenuLink item={item} state={state} onClose={onClose} data-testid={`${baseTestId}.${item.key}`}>
                    {node.rendered}
                </MenuLink>
            </LogProvider>
        );
    }

    if (isSubMenu(item)) {
        return (
            <LogProvider>
                <SubMenu
                    item={item}
                    state={state}
                    onClose={onClose}
                    label={node.textValue}
                    data-testid={`${baseTestId}.${item.key}`}
                    logClick={logClick}
                    subMenuTrigger={subMenuTrigger}
                    closeMenuTimeoutRef={closeMenuTimeoutRef}
                    wasSubMenuHoveredRef={wasSubMenuHoveredRef}
                >
                    {node.rendered}
                </SubMenu>
            </LogProvider>
        );
    }

    throw new Error('Не поддерживаемый элемент');
};
//</editor-fold>

//<editor-fold desc="MenuListWrapper">
export interface MenuListWrapperProps<ValueType extends string, SectionType extends string>
    extends Omit<MenuProps<ValueType, SectionType>, 'onOpenChange' | 'onAction' | 'pinnedSectionAfter'>,
        Loggable {
    onClose?: () => void;
    logClick?: LoggerClickHandler;
    pinnedSectionAfter?: ReactNode;
    subMenuTrigger: 'click' | 'hover';
    onMouseMove?: MouseEventHandler;
}

const getRenderItems = <ValueType extends string, SectionType extends string>() =>
    getDefaultRenderer<MenuSingleItem<ValueType>, MenuItemSection<ValueType, SectionType>>(getMenuItemKey);

export const MenuListWrapper = genericMemo(
    forwardRef(function MenuListWrapper<ValueType extends string, SectionType extends string>(
        props: MenuListWrapperProps<ValueType, SectionType>,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) {
        const {
            size,
            className,
            addonBefore,
            addonAfter,
            width,
            items,
            onClose,
            'data-testid': dataTestId = 'Menu',
            logClick,
            subMenuTrigger,
        } = props;

        const ref = useRef(null);
        const scrollClass = getScrollbarClassName();
        const { onMenuAction } = useContext(MenuContext);
        const hasIconLeft = containsIconLeft<
            ValueType,
            SectionType,
            MenuSingleItem<ValueType>,
            MenuItemSection<ValueType, SectionType>
        >(items);

        const state = useMenuState<MenuAnyItem<ValueType, SectionType>>({
            ...props,
            selectionMode: 'none',
            children: getRenderItems(),
            disabledKeys: getDisabledKeys<
                ValueType,
                SectionType,
                MenuSingleItem<ValueType>,
                MenuItemSection<ValueType, SectionType>
            >(items, getMenuItemKey),
        });
        const { menuProps } = useMenu(
            {
                ...props,
                onAction: key => {
                    // Логирует клики по всем элементам меню
                    logClick?.({ type: 'action', value: key });
                    // Контекст не принимет generic тип
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
                    onMenuAction?.(key as string);
                },
            },
            state,
            ref,
        );
        const wasSubMenuHoveredRef: React.MutableRefObject<boolean> = useRef(false);
        const closeMenuTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null> = useRef(null);
        const wrapperRef = useObjectRef(forwardedRef);

        // Засекает таймаут закрытия подменю при наведении на предыдущий уровень меню,
        // если до этого наводились на подменю
        // Нужно, чтобы закрыть подменю, если вернули курсор на произвольную часть меню предыдущего уровня
        // Костыль – сделано через mouseMove, так как hoverStart на предыдущем уровне не срабатывает
        // – потому что технически hoverEnd с него не происходит – следующий уровень меню наложен на предыдущий сверху
        const onMouseMove: MouseEventHandler<HTMLDivElement> = event => {
            props.onMouseMove?.(event);

            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- target можно так использовать
            if (wrapperRef.current?.contains(event.target as Node)) {
                if (wasSubMenuHoveredRef.current) {
                    wasSubMenuHoveredRef.current = false;

                    if (closeMenuTimeoutRef.current) {
                        clearTimeout(closeMenuTimeoutRef.current);
                        closeMenuTimeoutRef.current = null;
                    }

                    closeMenuTimeoutRef.current = setTimeout(() => {
                        state.closeSubmenus();
                    }, SUBMENU_OPEN_CLOSE_DELAY);
                }
            }
        };

        const menuItems = Array.from(state.collection).map(node => (
            <MenuItem
                node={node}
                state={state}
                onClose={onClose}
                baseTestId={dataTestId}
                key={node.value ? getMenuItemKey(node.value) : ''}
                subMenuTrigger={subMenuTrigger}
                closeMenuTimeoutRef={closeMenuTimeoutRef}
                wasSubMenuHoveredRef={wasSubMenuHoveredRef}
            />
        ));

        const listSize = isMobile() ? 'm' : size;
        const contextValue = useMemo(
            () => ({
                size: listSize,
            }),
            [listSize],
        );

        return (
            <ListBoxContext.Provider value={contextValue}>
                <ListContext.Provider value={contextValue}>
                    <div
                        className={cnMenu('wrapper')}
                        onMouseMove={!isMobile() && subMenuTrigger === 'hover' ? onMouseMove : undefined}
                        ref={wrapperRef}
                    >
                        <TooltipContainerContext.Provider value={wrapperRef}>
                            <ListBoxBase
                                size={size}
                                width={width}
                                className={cnMenu(null, [cnListBox({ 'has-icon-left': hasIconLeft }, [className])])}
                            >
                                {addonBefore}
                                {props.pinnedSection ? (
                                    <>
                                        <ListSection>{props.pinnedSection}</ListSection>
                                        {items.length !== 0 && <ListSection />}
                                    </>
                                ) : null}
                                <div
                                    {...menuProps}
                                    className={cnListBox('list', [scrollClass])}
                                    ref={ref}
                                    data-testid={dataTestId}
                                >
                                    {menuItems}
                                </div>
                                {props.pinnedSectionAfter ? (
                                    <>
                                        {items.length !== 0 && !props.pinnedSection && <ListSection />}
                                        <ListSection>{props.pinnedSectionAfter}</ListSection>
                                    </>
                                ) : null}
                                {addonAfter}
                            </ListBoxBase>
                        </TooltipContainerContext.Provider>
                    </div>
                </ListContext.Provider>
            </ListBoxContext.Provider>
        );
    }),
);
//</editor-fold>

export const Menu = genericMemo(function Menu<ValueType extends string, SectionType extends string = string>(
    props: MenuProps<ValueType, SectionType>,
) {
    const {
        'data-testid': dataTestId = 'Menu',
        logNode,
        pinnedSectionAfter: propsPinnedSectionAfter,
        subMenuTrigger = 'click',
    } = props;
    const triggerRef = useRef<HTMLElement>(null);
    const popupRef = useRef(null);
    const { LogProvider, logClick } = useLogger('Menu', logNode);
    const menuContextValue = useMemo(
        () => ({
            // Контекст не принимет generic тип
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
            onMenuAction: props.onAction as ((key: string) => void) | undefined,
            shouldCloseOnSelect: props.shouldCloseOnSelect,
        }),
        [props.shouldCloseOnSelect, props.onAction],
    );

    const state = useMenuTriggerState({
        ...props,
        trigger: 'press',
    });

    const trigger = Children.only(props.trigger);
    const openMenu = useCallback(() => {
        logClick({ type: 'open' });
        state.toggle();
    }, [logClick, state]);

    const closeMenu = useCallback(() => {
        logClick({ type: 'close' });
        state.close();
    }, [logClick, state]);

    const handlers = useMemo(
        () => ({
            onPress: chain(trigger?.props.onPress, openMenu),
            onClick: chain(trigger?.props.onClick, openMenu),
            ref: trigger?.ref ? mergeRefs(trigger.ref, triggerRef) : triggerRef,
        }),
        [openMenu, trigger?.props.onClick, trigger?.props.onPress, trigger?.ref, triggerRef],
    );

    useInteractOutside({
        onInteractOutside: event => {
            const target = event.target;
            const popups = Array.from(document.querySelectorAll('.dc-Menu__wrapper'));

            if (!(target instanceof Node)) {
                return;
            }

            const isTrigger = triggerRef.current?.contains(target);
            const shouldClose = !popups.some(popup => popup.contains(target)) && !isTrigger;
            if (shouldClose) {
                closeMenu();
            }
        },
        ref: popupRef,
        isDisabled: isMobile(),
    });

    const pinnedSectionAfter = useMemo(
        () =>
            typeof propsPinnedSectionAfter === 'function'
                ? propsPinnedSectionAfter(closeMenu)
                : propsPinnedSectionAfter,
        [propsPinnedSectionAfter, closeMenu],
    );

    const menuContent = useMemo(() => {
        const menuList = (
            <MenuContext.Provider value={menuContextValue}>
                <MenuListWrapper
                    {...props}
                    pinnedSectionAfter={pinnedSectionAfter}
                    onClose={closeMenu}
                    size={isMobile() ? 'm' : props.size}
                    width={isMobile() ? 'max' : props.width}
                    logClick={logClick}
                    subMenuTrigger={subMenuTrigger}
                />
            </MenuContext.Provider>
        );

        if (isMobile()) {
            return (
                <Drawer
                    isOpen={state.isOpen}
                    onClose={closeMenu}
                    header={
                        <DrawerHeader
                            onCancelClick={closeMenu}
                            title={props.label}
                            data-testid={`${dataTestId}.Header`}
                        />
                    }
                    data-testid={`${dataTestId}.Drawer`}
                >
                    {menuList}
                </Drawer>
            );
        }

        return (
            state.isOpen && (
                <Popup
                    ref={popupRef}
                    isOpen={state.isOpen}
                    onClose={closeMenu}
                    ariaType={'menu'}
                    triggerRef={triggerRef}
                    placement={props.placement}
                    isModal={false}
                    matchTriggerWidth={props.width === 'max'}
                    maxHeight={props.maxHeight}
                    maxWidth={props.maxWidth}
                >
                    {menuList}
                </Popup>
            )
        );
    }, [dataTestId, menuContextValue, props, closeMenu, state.isOpen, logClick, pinnedSectionAfter, subMenuTrigger]);

    return (
        <LogProvider>
            {trigger ? cloneElement(trigger, handlers) : null}
            {menuContent}
        </LogProvider>
    );
});
